import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Eye, EyeOff, Save } from "lucide-react";

/**
 * Dialog Casa Madre: gestisce la SCHEDA TECNICA del prodotto madre.
 *
 * Sezioni gestite (ognuna con toggle di visibilità in vetrina B2C):
 *   - Descrizione lunga
 *   - Dimensioni (L/P/H/Ø/seduta/peso)
 *   - Materiali (chip list)
 *   - Specifiche custom (lista key/value)
 *   - Garanzia (mesi)
 *   - Lead time (giorni)
 *   - Varianti correnti (toggle che mostra/nasconde le scelte attive con thumbnail dalla libreria)
 *
 * Props:
 *   - product: oggetto prodotto (id, nome, sku_madre)
 *   - onClose(): chiude
 */
const FEATURES = [
  { key: "descrizione", label: "Descrizione" },
  { key: "dimensioni", label: "Dimensioni" },
  { key: "materiali", label: "Materiali" },
  { key: "specifiche", label: "Specifiche tecniche" },
  { key: "varianti_correnti", label: "Varianti scelte (con thumbnail)" },
  { key: "garanzia", label: "Garanzia" },
  { key: "lead_time", label: "Tempi di consegna" },
];

const DIM_FIELDS = [
  { key: "larghezza_cm", label: "Larghezza", unit: "cm" },
  { key: "profondita_cm", label: "Profondità", unit: "cm" },
  { key: "altezza_cm", label: "Altezza", unit: "cm" },
  { key: "diametro_cm", label: "Diametro Ø", unit: "cm" },
  { key: "seduta_cm", label: "Altezza seduta", unit: "cm" },
  { key: "peso_kg", label: "Peso", unit: "kg" },
];

export default function CaratteristicheDialog({ product, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [descrizione, setDescrizione] = useState("");
  const [dimensioni, setDimensioni] = useState({});
  const [materiali, setMateriali] = useState([]);
  const [materialeInput, setMaterialeInput] = useState("");
  const [specifiche, setSpecifiche] = useState([]);
  const [garanzia, setGaranzia] = useState(24);
  const [leadTime, setLeadTime] = useState(45);
  const [visibility, setVisibility] = useState({
    descrizione: true, dimensioni: true, materiali: true,
    specifiche: true, varianti_correnti: true, garanzia: true, lead_time: true,
  });

  useEffect(() => {
    if (!product?.id) return;
    (async () => {
      try {
        const r = await api.get(`/products/${product.id}/caratteristiche`);
        const d = r.data || {};
        setDescrizione(typeof d.descrizione_long === "string" ? d.descrizione_long : "");
        setDimensioni(d.dimensioni && typeof d.dimensioni === "object" && !Array.isArray(d.dimensioni) ? d.dimensioni : {});
        setMateriali(Array.isArray(d.materiali) ? d.materiali : []);
        setSpecifiche(Array.isArray(d.specifiche) ? d.specifiche : []);
        setGaranzia(d.garanzia_mesi ?? 24);
        setLeadTime(d.lead_time_gg ?? 45);
        setVisibility({
          descrizione: true, dimensioni: true, materiali: true, specifiche: true,
          varianti_correnti: true, garanzia: true, lead_time: true,
          ...(d.feature_visibility && typeof d.feature_visibility === "object" ? d.feature_visibility : {}),
        });
      } catch (e) {
        toast.error("Errore caricamento: " + (e.response?.data?.detail || e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [product?.id]);

  const addMateriale = () => {
    const v = materialeInput.trim();
    if (!v) return;
    if (materiali.includes(v)) {
      toast.info("Materiale già presente");
      return;
    }
    setMateriali([...materiali, v]);
    setMaterialeInput("");
  };

  const removeMateriale = (i) => setMateriali(materiali.filter((_, idx) => idx !== i));

  const addSpec = () => setSpecifiche([...specifiche, { key: "", value: "" }]);
  const updateSpec = (i, field, value) => setSpecifiche(specifiche.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  const removeSpec = (i) => setSpecifiche(specifiche.filter((_, idx) => idx !== i));

  const toggleVis = (key) => setVisibility((v) => ({ ...v, [key]: !v[key] }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.id}/caratteristiche`, {
        descrizione_long: descrizione,
        dimensioni,
        materiali,
        specifiche: specifiche.filter((s) => s.key && s.value),
        garanzia_mesi: Number(garanzia) || 0,
        lead_time_gg: Number(leadTime) || 0,
        feature_visibility: visibility,
      });
      toast.success("Caratteristiche salvate");
      onClose && onClose(true);
    } catch (e) {
      toast.error("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => onClose && onClose(false)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-auto" data-testid="caratteristiche-dialog">
        <DialogHeader>
          <DialogTitle>
            Scheda tecnica — <span className="font-mono text-xs">{product?.sku_madre}</span> {product?.nome}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Visibility legend */}
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
              💡 Per ogni sezione puoi scegliere se mostrarla in vetrina B2C col toggle <Eye className="inline w-3 h-3" />/<EyeOff className="inline w-3 h-3" />.
            </div>

            {/* DESCRIZIONE */}
            <Section title="Descrizione lunga" visKey="descrizione" visibility={visibility} onToggle={toggleVis} testid="sec-descrizione">
              <textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
                rows={4}
                placeholder="Racconta il prodotto: design, ispirazione, contesto d'uso..."
                className="w-full text-sm border border-border rounded-sm p-2 resize-y"
                data-testid="car-descrizione" />
            </Section>

            {/* DIMENSIONI */}
            <Section title="Dimensioni & peso" visKey="dimensioni" visibility={visibility} onToggle={toggleVis} testid="sec-dimensioni">
              <div className="grid grid-cols-3 gap-3">
                {DIM_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.label}</label>
                    <div className="flex items-center">
                      <input type="number" step="0.1" min="0" value={dimensioni[f.key] ?? ""}
                        onChange={(e) => setDimensioni({ ...dimensioni, [f.key]: e.target.value })}
                        placeholder="—"
                        className="flex-1 h-9 text-sm border border-border rounded-sm px-2"
                        data-testid={`car-dim-${f.key}`} />
                      <span className="ml-1.5 text-[10px] text-muted-foreground w-6">{f.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* MATERIALI */}
            <Section title="Materiali" visKey="materiali" visibility={visibility} onToggle={toggleVis} testid="sec-materiali">
              <div className="flex gap-2 mb-2">
                <input type="text" value={materialeInput}
                  onChange={(e) => setMaterialeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMateriale(); } }}
                  placeholder="es. Velluto, Rovere massello, Acciaio inox..."
                  className="flex-1 h-9 text-sm border border-border rounded-sm px-2"
                  data-testid="car-mat-input" />
                <button onClick={addMateriale} className="h-9 px-3 bg-brand text-white rounded-sm text-xs font-medium inline-flex items-center gap-1 hover:bg-brand-hover" data-testid="car-mat-add">
                  <Plus className="w-3.5 h-3.5" /> Aggiungi
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {materiali.length === 0 && <span className="text-xs text-muted-foreground italic">Nessun materiale aggiunto</span>}
                {materiali.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-border rounded-sm text-xs" data-testid={`car-mat-chip-${i}`}>
                    {m}
                    <button onClick={() => removeMateriale(i)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </Section>

            {/* SPECIFICHE */}
            <Section title="Specifiche tecniche custom" visKey="specifiche" visibility={visibility} onToggle={toggleVis} testid="sec-specifiche">
              <div className="space-y-1.5">
                {specifiche.length === 0 && <div className="text-xs text-muted-foreground italic mb-2">Nessuna specifica. Aggiungi voci come Origine = Italia, Certificazione = FSC, ecc.</div>}
                {specifiche.map((s, i) => (
                  <div key={i} className="flex gap-1.5">
                    <input type="text" value={s.key} onChange={(e) => updateSpec(i, "key", e.target.value)}
                      placeholder="Nome (es. Origine)"
                      className="flex-1 h-9 text-sm border border-border rounded-sm px-2"
                      data-testid={`car-spec-k-${i}`} />
                    <input type="text" value={s.value} onChange={(e) => updateSpec(i, "value", e.target.value)}
                      placeholder="Valore (es. Italia)"
                      className="flex-1 h-9 text-sm border border-border rounded-sm px-2"
                      data-testid={`car-spec-v-${i}`} />
                    <button onClick={() => removeSpec(i)} className="h-9 w-9 inline-flex items-center justify-center text-red-500 hover:bg-red-50 border border-border rounded-sm" data-testid={`car-spec-del-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button onClick={addSpec} className="h-9 w-full border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-foreground rounded-sm inline-flex items-center justify-center gap-1" data-testid="car-spec-add">
                  <Plus className="w-3.5 h-3.5" /> Aggiungi specifica
                </button>
              </div>
            </Section>

            {/* GARANZIA + LEAD TIME */}
            <div className="grid grid-cols-2 gap-3">
              <Section title="Garanzia" visKey="garanzia" visibility={visibility} onToggle={toggleVis} testid="sec-garanzia">
                <div className="flex items-center gap-2">
                  <input type="number" min="0" value={garanzia}
                    onChange={(e) => setGaranzia(e.target.value)}
                    className="w-24 h-9 text-sm border border-border rounded-sm px-2"
                    data-testid="car-garanzia" />
                  <span className="text-sm text-muted-foreground">mesi</span>
                </div>
              </Section>
              <Section title="Lead time" visKey="lead_time" visibility={visibility} onToggle={toggleVis} testid="sec-leadtime">
                <div className="flex items-center gap-2">
                  <input type="number" min="0" value={leadTime}
                    onChange={(e) => setLeadTime(e.target.value)}
                    className="w-24 h-9 text-sm border border-border rounded-sm px-2"
                    data-testid="car-leadtime" />
                  <span className="text-sm text-muted-foreground">giorni</span>
                </div>
              </Section>
            </div>

            {/* VARIANTI CORRENTI — solo toggle (il contenuto è generato in vetrina automaticamente) */}
            <Section title="Mostra varianti correnti (tessuto/piede/metallo + thumbnail libreria)" visKey="varianti_correnti" visibility={visibility} onToggle={toggleVis} testid="sec-varianti">
              <div className="text-xs text-muted-foreground">
                Quando il cliente sceglie un tessuto/piede/metallo, in vetrina compaiono il nome e l&apos;immagine dalla libreria. Disattiva se vuoi una scheda più minimale.
              </div>
            </Section>

            {/* Save */}
            <div className="flex gap-2 sticky bottom-0 bg-white border-t border-border pt-3 -mx-6 px-6 -mb-6 pb-5">
              <button onClick={() => onClose && onClose(false)} className="flex-1 h-10 border border-border rounded-sm text-sm" data-testid="car-cancel">
                Annulla
              </button>
              <button onClick={save} disabled={saving}
                className="flex-1 h-10 bg-brand text-white rounded-sm text-sm font-medium hover:bg-brand-hover disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                data-testid="car-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salva scheda tecnica
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, visKey, visibility, onToggle, testid, children }) {
  const visible = visibility[visKey] !== false;
  return (
    <div className="border border-border rounded-sm" data-testid={testid}>
      <div className="px-3 py-2 border-b border-border bg-neutral-50 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
        <button onClick={() => onToggle(visKey)}
          title={visible ? "Visibile in vetrina B2C" : "Nascosto in vetrina B2C"}
          className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-sm transition-colors ${visible ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-neutral-200 text-neutral-600 hover:bg-neutral-300"}`}
          data-testid={`${testid}-vis`}>
          {visible ? <><Eye className="w-3 h-3" /> Visibile</> : <><EyeOff className="w-3 h-3" /> Nascosto</>}
        </button>
      </div>
      <div className={`p-3 ${!visible ? "opacity-50" : ""}`}>{children}</div>
    </div>
  );
}
